// Service Worker Registration Utility
// Handles registration, updates, and offline detection

interface ServiceWorkerConfig {
    onSuccess?: (registration: ServiceWorkerRegistration) => void;
    onUpdate?: (registration: ServiceWorkerRegistration) => void;
    onError?: (error: Error) => void;
}

class ServiceWorkerManager {
    private registration: ServiceWorkerRegistration | null = null;
    private isOnline = navigator.onLine;

    constructor() {
        this.setupOnlineOfflineDetection();
    }

    // Register service worker
    async register(config: ServiceWorkerConfig = {}): Promise<void> {
        if ('serviceWorker' in navigator) {
            try {
                this.registration = await navigator.serviceWorker.register('/sw.js', {
                    scope: '/',
                });

                console.log('[SW] Service Worker registered successfully');

                // Handle updates
                this.registration.addEventListener('updatefound', () => {
                    const newWorker = this.registration!.installing;
                    if (newWorker) {
                        newWorker.addEventListener('statechange', () => {
                            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                                // New content is available, notify user
                                config.onUpdate?.(this.registration!);
                            }
                        });
                    }
                });

                // Handle controller change (new SW activated)
                navigator.serviceWorker.addEventListener('controllerchange', () => {
                    console.log('[SW] Service Worker updated, reloading page...');
                    window.location.reload();
                });

                config.onSuccess?.(this.registration);
            } catch (error) {
                console.error('[SW] Service Worker registration failed:', error);
                config.onError?.(error as Error);
            }
        } else {
            console.warn('[SW] Service Worker not supported in this browser');
        }
    }

    // Unregister service worker
    async unregister(): Promise<void> {
        if ('serviceWorker' in navigator) {
            try {
                const registrations = await navigator.serviceWorker.getRegistrations();
                await Promise.all(
                    registrations.map(registration => registration.unregister())
                );
                console.log('[SW] Service Worker unregistered');
            } catch (error) {
                console.error('[SW] Service Worker unregistration failed:', error);
            }
        }
    }

    // Check if app is online
    get isOnlineStatus(): boolean {
        return this.isOnline;
    }

    // Get cache storage info
    async getCacheInfo(): Promise<{
        static: number;
        dynamic: number;
        api: number;
        total: number;
    }> {
        if (!('caches' in window)) {
            return { static: 0, dynamic: 0, api: 0, total: 0 };
        }

        try {
            const cacheNames = await caches.keys();
            let staticSize = 0;
            let dynamicSize = 0;
            let apiSize = 0;

            for (const cacheName of cacheNames) {
                const cache = await caches.open(cacheName);
                const keys = await cache.keys();

                if (cacheName.includes('static')) {
                    staticSize += keys.length;
                } else if (cacheName.includes('dynamic')) {
                    dynamicSize += keys.length;
                } else if (cacheName.includes('api')) {
                    apiSize += keys.length;
                }
            }

            return {
                static: staticSize,
                dynamic: dynamicSize,
                api: apiSize,
                total: staticSize + dynamicSize + apiSize,
            };
        } catch (error) {
            console.error('[SW] Failed to get cache info:', error);
            return { static: 0, dynamic: 0, api: 0, total: 0 };
        }
    }

    // Clear all caches
    async clearCache(): Promise<void> {
        if (!('caches' in window)) return;

        try {
            const cacheNames = await caches.keys();
            await Promise.all(
                cacheNames.map(cacheName => caches.delete(cacheName))
            );
            console.log('[SW] All caches cleared');
        } catch (error) {
            console.error('[SW] Failed to clear caches:', error);
        }
    }

    // Setup online/offline detection
    private setupOnlineOfflineDetection(): void {
        window.addEventListener('online', () => {
            this.isOnline = true;
            console.log('[SW] App is online');
            // You could trigger sync here
        });

        window.addEventListener('offline', () => {
            this.isOnline = false;
            console.log('[SW] App is offline');
        });
    }

    // Send message to service worker
    async sendMessage(message: any): Promise<any> {
        if (!this.registration?.active) return null;

        return new Promise((resolve) => {
            const messageChannel = new MessageChannel();

            messageChannel.port1.onmessage = (event) => {
                resolve(event.data);
            };

            this.registration!.active!.postMessage(message, [messageChannel.port2]);
        });
    }
}

// Create singleton instance
export const serviceWorkerManager = new ServiceWorkerManager();

// Default registration function for easy use
export const registerServiceWorker = (config?: ServiceWorkerConfig) => {
    return serviceWorkerManager.register(config);
};

export default serviceWorkerManager;